import React from "react";
import type { ReactNode } from "react";

import { cn } from "./cn";

export interface BrowserMockupProps {
  /** Address bar text — e.g. `${subdomain}.zournalhq.com`. */
  url: string;
  children: ReactNode;
  className?: string;
}

// Minimal browser-chrome frame for the right preview panel — just enough
// chrome (three dots + an address bar) to read as "a browser", never enough
// to distract from the page taking shape inside it.
export function BrowserMockup({ url, children, className }: BrowserMockupProps) {
  return (
    <div className={cn("flex flex-1 flex-col overflow-hidden rounded-lg border bg-card", className)}>
      <div className="flex items-center gap-1.5 border-b bg-muted/40 px-3 py-2">
        <span className="size-2 rounded-full bg-muted-foreground/25" />
        <span className="size-2 rounded-full bg-muted-foreground/25" />
        <span className="size-2 rounded-full bg-muted-foreground/25" />
        <div className="ml-1.5 flex-1 truncate rounded-full bg-background px-2.5 py-1 text-center text-[11px] text-muted-foreground">
          {url}
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">{children}</div>
    </div>
  );
}

// The page-in-progress convention: every section renders something — real
// content once its step is reached, a skeleton placeholder before that — so
// the mockup always looks like it's forming, never empty.
export function MockupSkeletonLine({ className }: { className?: string }) {
  return <div className={cn("h-2.5 rounded-full bg-muted", className)} />;
}

export function MockupSkeletonPill({ className }: { className?: string }) {
  return <div className={cn("h-6 w-16 shrink-0 rounded-full bg-muted", className)} />;
}

export function MockupSkeletonCircle({ className }: { className?: string }) {
  return <div className={cn("size-7 shrink-0 rounded-full bg-muted", className)} />;
}

export function MockupStatusDot({ active, className }: { active: boolean; className?: string }) {
  return (
    <span className={cn("size-2 shrink-0 rounded-full", active ? "bg-emerald-500" : "bg-muted-foreground/30", className)} />
  );
}
